import React = require("react");
import { SingleColumnLayout } from '../../../designSystem/layouts/SingleColumnLayout';
import { LoadingPageOverlay } from "../../../designSystem/LoadingPageOverlay";
import * as dateMath from 'date-arithmetic';
import { txt } from "../../../txt";
import { getAgendaMeetingActivities, createActivity } from "../../../api/activityClientApi";
import { ActivityListingView } from "./ActivityListingView";
import { AgendaMeetingActivityListing, rng } from "../../../amorg-common";
import { AmorgMenuContent } from "../../../designSystem/amorgMenu";
import { Redirect } from "react-router-dom";
import { GenericActivityListingView } from "./GenericActivityListingView";
import { iconName } from "../../../designSystem/icons/Icons";


type State = {
	loading:boolean,
	activities:AgendaMeetingActivityListing[],
    redirectId:string
}

type AgendaMeetingListingViewProps = {
    type: 'WorkgroupMeeting' | 'AnnualWorkgroupStatus',
    headerIcon?:iconName,
    pageHeader:string,
    createNewButtonText:string,
    currentActivitiesHeader:string,
    pastActivitiesHeader:string,
}
export function AgendaMeetingListingView(props:AgendaMeetingListingViewProps): JSX.Element
{
	const [state, setState] = React.useState<State>({
        loading:true,
        activities:[],
        redirectId:''
    });

    React.useEffect(()=>{
        const getData = async () => {
            const activities = await getAgendaMeetingActivities(props.type);
            activities.sort((a,b)=> b.start.getTime() - a.start.getTime());
            // Get activities convert start and end from strings (JSON transport) to dates
            setState({activities, loading:false, redirectId:''});
        }
        getData();
    },[])

    async function handleClickNew()
    {
        const now = new Date();

        const activityId = rng.getRandomId();
        const startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours()+1, 0, 0, 0);
        const endDate = dateMath.add(startDate, 1, 'hours');

        setState({...state, loading:true});

        const result = await createActivity(
            activityId,
            props.type,
            txt.getDynamic(`NewActivityType_${props.type}`),
            '',
            startDate,
            endDate,
            ''
        );

        if(result.isSuccess){
            setState({...state, redirectId:activityId});
        }
    }

    const redirectUrl = props.type === 'WorkgroupMeeting' 
        ? '/workgroupmeetings/' +  (state.redirectId || '')
        : '/annualworkgroupstatus/' + (state.redirectId || '')

    return (
        <SingleColumnLayout
            noDivider
            contentContainer={
                <>
                    {state.redirectId !== '' &&
                        <Redirect to={redirectUrl} />
                    }

                    <LoadingPageOverlay loading={state.loading} />
                    
                    <GenericActivityListingView
                        headerIcon={props.headerIcon}
                        headerText={props.currentActivitiesHeader}
                        pastHeaderText={props.pastActivitiesHeader}
                        newActivityButtonText={props.createNewButtonText}
                        activities={state.activities}
                        onClick={(id) => setState({...state, redirectId:id})}
                        onClickNew={handleClickNew} />
                </>
            }
        />

	);
}

type AgendaMeetingViewProps = {
    activity:AgendaMeetingActivityListing
}

export function AgendaMeetingView({activity}:AgendaMeetingViewProps):JSX.Element
{
    const url = activity.activityType === 'WorkgroupMeeting' 
        ? '/workgroupmeetings/' +  activity.id
        : '/annualworkgroupstatus/' + activity.id

    return (
        <ActivityListingView
            title={activity.title}
            start={activity.start}
            end={activity.end}
            href={url} 
            menuProps={{
                content: [
                    getParticipantMenuItemProps(activity),
                    getAgendaItemMenuItemProps(activity),
                    getActionPointMenuItemProps(activity)
                ]
            }}
        />
    );
}

function getParticipantMenuItemProps(activity:AgendaMeetingActivityListing):AmorgMenuContent
{

    return {
        type:'leadingContent',
        styleVariant: activity.participantCount === 0 ? 'destructive' : 'default',
        leadingContent:{ type:'icon', iconName: activity.participantCount === 0 ? 'actions_close' : 'interface_user_team' },
        primaryContent:{ type:'text', text:activity.participantCount === 0 ? 'Ingen deltagere' : activity.participantCount === 1 ? activity.participantCount + " deltager" : activity.participantCount + " deltagere"}
    }
}

function getAgendaItemMenuItemProps(activity:AgendaMeetingActivityListing):AmorgMenuContent
{
    return {
        type:'leadingContent',
        styleVariant:activity.agendaItemCount===0 ? 'destructive' : 'default',
        leadingContent:{ type:'icon', iconName:activity.agendaItemCount===0 ? 'actions_close' : 'fact_check' },
        primaryContent:{ type:'text', text:activity.agendaItemCount===0 ? 'Ingen agendapunkter' : activity.agendaItemCount === 1 ? activity.agendaItemCount + " agendapunkt" : activity.agendaItemCount + " agendapunkter"}
    };
}


function getActionPointMenuItemProps(activity:AgendaMeetingActivityListing):AmorgMenuContent
{
    
    let trailingText = '';
    let labelText = '';
    let apStyle;


    if(activity.actionPointDoneCount === activity.actionPointCount){
        trailingText = "";
        labelText = activity.actionPointDoneCount + " ud af " + activity.actionPointDoneCount + (activity.actionPointCount == 1 ? " handlepunkt er afsluttet" : " handlepunkter er afsluttede");
        apStyle = 'default'
    }

    else if(activity.actionPointDoneCount >= 1 && activity.actionPointDeadlinePassedCount === 0 && activity.actionPointDoneCount !== activity.actionPointCount){
        trailingText = '';
        labelText = (activity.actionPointCount - activity.actionPointDoneCount) + ' ud af ' + activity.actionPointCount + ((activity.actionPointCount) === 1 ? " handlepunkt er åbent" : ' handlepunkter er åbne');
        apStyle = 'default'
    }

    else if(activity.actionPointDeadlinePassedCount >= 1){
        
        trailingText = activity.actionPointDeadlinePassedCount + (activity.actionPointDeadlinePassedCount === 1 ? " deadline er overskredet" : ' deadlines er overskredne');
        labelText = (activity.actionPointCount - activity.actionPointDoneCount) + ' ud af ' + activity.actionPointCount + (activity.actionPointCount == 1 ? ' handlepunkt er' : ' handlepunkter er') + ((activity.actionPointCount - activity.actionPointDoneCount) === 1 ? " åbent" : " åbne");
        apStyle = 'destructive'    

    } else {
        trailingText = "";
        labelText = (activity.actionPointCount - activity.actionPointDoneCount) + ' ud af ' + activity.actionPointCount + (activity.actionPointCount == 1 ? ' handlepunkt er' : ' handlepunkter er') + ((activity.actionPointCount - activity.actionPointDoneCount) === 1 ? " åbent" : " åbne");
        apStyle = 'default'
    }
    

    return {
        type:'leadingTrailingContent',
        styleVariant: activity.actionPointCount===0 ? 'destructive' : apStyle,
        leadingContent: { type:'icon', iconName:activity.actionPointCount===0 ? 'actions_close' : 'actions_done' },
        primaryContent:{ type:'text', text: activity.actionPointCount===0 ? 'Ingen handlepunkter' : labelText },
        trailingContent: { type:'text', text: trailingText}
    };
    
}